import React, { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { HiOutlineFire } from "react-icons/hi2";
import communityService from "../../services/communityService";
import followService from "../../services/followService";
import commentService from "../../services/commentService";
import UserCard from "./UserCard";
import RecipeCard from "./RecipeCard";
import CommentItem from "./CommentItem";
import { useRecipesTranslation } from "../common/RecipeTranslator";
import { useRecipePopup } from "../../contexts/RecipePopupContext.jsx";
import apiConfig from "../../config/api.js";
import { CardGridLoading } from "../common/CardGridLoading";

/**
 * Onglet Explorer de la communauté
 * Découvrir les recettes populaires, nouveaux utilisateurs et commentaires récents
 */
const ExplorerTab = ({ user, token }) => {
  const { t } = useLanguage();
  const { openRecipePopup } = useRecipePopup();
  const [topRecipes, setTopRecipes] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [recentComments, setRecentComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("month");

  useEffect(() => {
    loadExplorerData();
  }, [selectedPeriod]);

  const loadExplorerData = async () => {
    setLoading(true);
    try {
      const [recipesData, usersData, commentsData] = await Promise.all([
        communityService.getTopRecipes(12, selectedPeriod),
        followService.getSuggestedUsers(8),
        commentService.getRecentCommunityComments(10),
      ]);

      setTopRecipes(recipesData.recipes || []);
      setSuggestedUsers(usersData.suggestions || []);
      setRecentComments(commentsData.comments || []);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Traduction automatique des recettes populaires
   */
  const { translatedRecipes: translatedTopRecipes, isTranslating } =
    useRecipesTranslation(topRecipes);

  if (loading) {
    return (
      <CardGridLoading
        message={t("loadingCommunity")}
        gridCols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        count={9}
      />
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Section des recettes populaires */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex items-center gap-2">
            <HiOutlineFire
              className="w-6 h-6 sm:w-7 sm:h-7 text-orange-400 shrink-0"
              aria-hidden
            />
            <span>{t("popularRecipes")}</span>
          </h2>

          {/* Sélecteur de période - scroll horizontal sur mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2 bg-ink-100 dark:bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 border border-ink-900/10 dark:border-white/20 overflow-x-auto w-full sm:w-auto justify-end">
            {[
              { value: "week", label: t("week") },
              { value: "month", label: t("month") },
              { value: "all", label: t("all") },
            ].map((period) => (
              <button
                key={period.value}
                onClick={() => setSelectedPeriod(period.value)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap touch-manipulation flex-shrink-0 ${
                  selectedPeriod === period.value
                    ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md"
                    : "text-ink-700 hover:bg-ink-100 dark:text-white dark:hover:bg-white/20"
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        {topRecipes.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {translatedTopRecipes.map((recipe) => (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                user={user}
                onRecipeClick={async (r) => {
                  try {
                    const token = localStorage.getItem("token");
                    const res = await fetch(
                      `${apiConfig.baseURL}/recipes/${r.id}`,
                      token
                        ? { headers: { Authorization: `Bearer ${token}` } }
                        : {},
                    );
                    const data = await res.json();
                    const full = res.ok && data?.data;
                    const merged = full ? { ...full, ...r } : r;
                    openRecipePopup(merged, user, {
                      onFavoriteToggle: loadExplorerData,
                    });
                  } catch (e) {
                    openRecipePopup(r, user, {
                      onFavoriteToggle: loadExplorerData,
                    });
                  }
                }}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12 bg-white dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 px-4">
            <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base">{t("noPopularRecipes")}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        {/* Section des utilisateurs suggérés */}
        <div className="lg:col-span-2">
          <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex items-center gap-2 mb-4 sm:mb-6">
            <span aria-hidden>👥</span>
            <span>{t("cooksToDiscover")}</span>
          </h2>

          {suggestedUsers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {suggestedUsers.map((suggestedUser) => (
                <UserCard
                  key={suggestedUser.id}
                  user={suggestedUser}
                  currentUser={user}
                  onFollowChange={loadExplorerData}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12 bg-white dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 px-4">
              <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base">{t("noSuggestions")}</p>
            </div>
          )}
        </div>

        {/* Section des commentaires récents */}
        <div className="lg:col-span-1">
          <h2 className="text-xl sm:text-2xl font-bold text-ink-900 dark:text-white flex items-center gap-2 mb-4 sm:mb-6">
            <span aria-hidden>💬</span>
            <span>{t("recentActivity")}</span>
          </h2>

          {recentComments.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {recentComments.slice(0, 5).map((comment) => (
                <CommentItem key={comment.id} comment={comment} compact />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12 bg-white dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl border border-ink-900/10 dark:border-white/20 px-4">
              <p className="text-ink-600 dark:text-white/90 text-sm sm:text-base">{t("noRecentActivity")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExplorerTab;
